import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "./siteData";

/* -------------------------------------------------------------------------- */
/*  SERVICES PAGE CONTENT  (Firestore document: site/services)                */
/*  Stored as { items: [...], updatedAt, updatedBy }. The public Services     */
/*  page will read this same document, so Publish = live.                     */
/*  `icon` is a key: search, droplets, gauge, sun, zap, wrench, leaf, home.   */
/*  The ids of the first six are what the diagram on the public page uses.    */
/* -------------------------------------------------------------------------- */

const IMG = {
  valley:
    "https://res.cloudinary.com/dj6wdsbtx/image/upload/v1791732731/Golden-Hour_Drilling_in_the_Valley_mcazyh.png",
  well:
    "https://res.cloudinary.com/dj6wdsbtx/image/upload/v1791732733/Golden-Hour_Water_Well_Operation_alcqbk.png",
  solar:
    "https://res.cloudinary.com/dj6wdsbtx/image/upload/v1791732733/Solar_Installation_at_Golden_Hour_fpk7wv.png",
};

// What the Services page shows today, so the manager opens pre-filled the first time.
export const DEFAULT_SERVICES = [
  {
    id: "survey",
    label: "Water survey and siting",
    short: "Survey",
    icon: "search",
    summary: "Know where to drill before any money goes into the ground.",
    what: "A survey studies your land to work out where water is most likely to be found. We visit the site, assess it, and recommend a drilling point, so you are not drilling on a guess. It is the step that helps you avoid putting a borehole in the wrong place.",
    included: [
      "A site visit and assessment of your land",
      "A recommended drilling point",
      "Honest advice, including when a site looks difficult",
      "A clear recommendation you can keep",
    ],
    goodFor: ["Anyone planning to drill", "Farms and large plots", "Homes"],
    img: IMG.well,
    pos: "30% center",
    alt: "Water well operation at golden hour",
  },
  {
    id: "borehole",
    label: "Borehole drilling",
    short: "Borehole drilling",
    icon: "droplets",
    summary: "Your own dependable water source, drilled and developed until it flows.",
    what: "A borehole is a narrow, deep well drilled down to the water stored in the ground. Our crew drills at the chosen point, lines the hole with casing so it stays open and clean, then develops it so the water runs properly before we hand it over.",
    included: [
      "Test hole and drilling at the chosen point",
      "Casing to keep the borehole open",
      "Development until the borehole flows",
      "Handover with a walk-through",
    ],
    goodFor: ["Homes", "Farms", "Businesses", "Institutions"],
    img: IMG.valley,
    pos: "center 60%",
    alt: "Drilling rig working in a valley at golden hour",
  },
  {
    id: "pump",
    label: "Pump installation",
    short: "Pump installation",
    icon: "gauge",
    summary: "The right pump for your borehole depth, yield and daily demand.",
    what: "The pump is what brings water from the borehole to where you use it. We match a submersible or surface pump to how deep the water is, how much the borehole gives and how much you need each day, then install and test it.",
    included: [
      "Pump sizing and selection",
      "Installation and connections",
      "Testing before we leave",
      "A walk-through of how to run it",
    ],
    goodFor: ["New boreholes", "Existing boreholes", "Replacing a failed pump"],
    img: IMG.well,
    pos: "80% 60%",
    alt: "Water well site at golden hour",
  },
  {
    id: "solar",
    label: "Solar installation",
    short: "Solar installation",
    icon: "sun",
    summary: "Panels, inverters and batteries sized for how you actually use power.",
    what: "A solar system turns sunlight into electricity for your home, farm or business. We size the panels, inverter and batteries to your real load, install them cleanly, and test everything before we leave.",
    included: [
      "Assessment of how much power you use",
      "Panels, inverter and battery sizing",
      "Clean mounting and wiring",
      "Testing and handover",
    ],
    goodFor: ["Homes", "Farms", "Businesses", "Backup power"],
    img: IMG.solar,
    pos: "center",
    alt: "Solar panels being installed at golden hour",
  },
  {
    id: "solarpump",
    label: "Solar water pumping",
    short: "Solar water pumping",
    icon: "zap",
    summary: "Pump water with the sun and cut fuel and grid costs.",
    what: "Instead of running a pump on diesel or grid power, a solar water pumping system runs it straight from panels. Water flows while the sun shines, and storage means it is ready when you need it.",
    included: [
      "Solar array sized to your pump",
      "Pump and controller matched together",
      "Installation and testing",
      "Advice on storage so water is ready on demand",
    ],
    goodFor: ["Irrigation", "Livestock", "Households", "Off-grid sites"],
    img: IMG.solar,
    pos: "75% 40%",
    alt: "Solar installation on site",
  },
  {
    id: "maintenance",
    label: "Maintenance and repairs",
    short: "Maintenance and repairs",
    icon: "wrench",
    summary: "Keep your water and power running, season after season.",
    what: "Pumps wear, panels get dirty and systems drift out of tune. Servicing and checks catch problems early, and repairs get you running again quickly when something fails.",
    included: [
      "System checks",
      "Pump servicing and replacement",
      "Panel and battery checks",
      "Fault finding and repair",
    ],
    goodFor: ["Homes", "Farms", "Businesses"],
    img: IMG.valley,
    pos: "20% 70%",
    alt: "Drilling crew at work",
  },
];

const servicesRef = () => doc(db, "site", "services");

// Same shape as loadLanding: { data, exists, updatedAt }. Here `data` is the list of services.
export async function loadServices() {
  const snap = await getDoc(servicesRef());
  const items = snap.exists() ? snap.data().items : null;
  if (!Array.isArray(items) || items.length === 0) {
    return { data: DEFAULT_SERVICES, exists: false, updatedAt: null };
  }
  return {
    data: items,
    exists: true,
    updatedAt: snap.data().updatedAt?.toDate?.() ?? null,
  };
}

export async function saveServices(items, by) {
  await setDoc(servicesRef(), {
    items,
    updatedAt: serverTimestamp(),
    updatedBy: by || "",
  });
}